import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Chip,
  Stack,
  Paper,
  Avatar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ArrowForward,
  AutoAwesome,
  MovieFilter,
  PhotoCamera,
  MusicNote,
  Brush,
  LocationCity
} from '@mui/icons-material';
import { URBAN_AGENTS, UrbanAgentType } from '../models/urbanAgents';

const getIconComponent = (agentType: UrbanAgentType) => {
  switch (agentType) {
    case UrbanAgentType.Animation:
      return <MovieFilter />;
    case UrbanAgentType.Documentary:
      return <PhotoCamera />;
    case UrbanAgentType.Music:
      return <MusicNote />;
    case UrbanAgentType.StreetArt:
      return <Brush />;
    case UrbanAgentType.General:
      return <LocationCity />;
    default:
      return <AutoAwesome />;
  }
};

const UrbanAgentsGallery = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Container 
      maxWidth="md" 
      sx={{ 
        py: { xs: 2, sm: 3, md: 4 },
        px: { xs: 1.5, sm: 2, md: 3 }
      }}
    >
      <Box textAlign="center" sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            mb: 2,
            fontSize: { xs: '1.75rem', sm: '2.125rem' }
          }}
        >
          Urban Culture AI Studio
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary" 
          paragraph
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          Explore specialized AI agents for urban culture visualization and prompt generation
        </Typography>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={2} 
          justifyContent="center" 
          alignItems="center"
          sx={{ mt: 3 }}
        >
          <Chip 
            icon={<AutoAwesome />} 
            label="5 Specialized Agents" 
            color="primary"
            size={isMobile ? 'small' : 'medium'}
          />
          <Chip 
            label="25+ Features" 
            color="secondary"
            size={isMobile ? 'small' : 'medium'}
          />
          <Chip 
            label="Multi-Era Support" 
            variant="outlined"
            size={isMobile ? 'small' : 'medium'}
          />
        </Stack>
      </Box>

      <Paper 
        sx={{ 
          p: { xs: 2, sm: 3 }, 
          mb: 4, 
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
          color: 'white'
        }}
      >
        <Typography 
          variant="h5" 
          gutterBottom
          sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
        >
          What are Urban Culture Agents?
        </Typography>
        <Typography 
          variant="body1" 
          paragraph
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          Our Urban Culture AI agents are specialized tools designed to help you create authentic, 
          diverse, and culturally-rich visual prompts. Each agent focuses on a specific aspect of 
          urban culture, from street art and music scenes to documentary photography and animation.
        </Typography>
        <Typography 
          variant="body2"
          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
        >
          These agents understand the nuances of different eras, regions, and styles, helping you 
          generate prompts that capture the essence of urban life across cultures and time periods.
        </Typography>
      </Paper>

      <Grid container spacing={3}>
        {URBAN_AGENTS.map((agent) => (
          <Grid item xs={12} md={6} key={agent.id}>
            <Card 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                transition: 'all 0.3s ease',
                borderTop: `4px solid ${agent.color}`,
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: theme.shadows[8]
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: { xs: 2, sm: 3 } }}>
                <Stack 
                  direction={{ xs: 'column', sm: 'row' }} 
                  spacing={2} 
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  sx={{ mb: 2 }}
                >
                  <Avatar 
                    sx={{ 
                      width: { xs: 48, sm: 56 }, 
                      height: { xs: 48, sm: 56 },
                      backgroundColor: agent.color,
                      fontSize: { xs: '24px', sm: '28px' }
                    }}
                  >
                    {agent.icon}
                  </Avatar>
                  <Box>
                    <Typography 
                      variant="h5" 
                      gutterBottom
                      sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                    >
                      {agent.name}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                    >
                      {agent.description}
                    </Typography>
                  </Box>
                </Stack>

                <Box sx={{ mt: 3 }}>
                  <Typography 
                    variant="subtitle2" 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                  >
                    Key Features:
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {agent.features.map((feature, index) => (
                      <Chip 
                        key={index}
                        label={feature}
                        size="small"
                        variant="outlined"
                        sx={{ 
                          borderColor: agent.color,
                          color: agent.color,
                          fontSize: { xs: '0.7rem', sm: '0.75rem' }
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              </CardContent>

              <CardActions sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Button 
                  fullWidth
                  variant="contained"
                  endIcon={<ArrowForward />}
                  onClick={() => navigate(`/urban-agents/${agent.id}`)}
                  sx={{ 
                    backgroundColor: agent.color,
                    minHeight: 44,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    '&:hover': {
                      backgroundColor: agent.color,
                      filter: 'brightness(0.9)'
                    }
                  }}
                >
                  Launch {agent.name}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, mt: 6, backgroundColor: 'action.hover' }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box textAlign="center">
              <Typography 
                variant="h4" 
                color="primary" 
                gutterBottom
                sx={{ fontSize: { xs: '2rem', sm: '2.125rem' } }}
              >
                🌍
              </Typography>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
              >
                Global Perspectives
              </Typography>
              <Typography 
                variant="body2"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
              >
                Generate prompts that reflect urban cultures from around the world, 
                from Tokyo's neon streets to NYC's graffiti walls.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box textAlign="center">
              <Typography 
                variant="h4" 
                color="secondary" 
                gutterBottom
                sx={{ fontSize: { xs: '2rem', sm: '2.125rem' } }}
              >
                🕰️
              </Typography>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
              >
                Era Evolution
              </Typography>
              <Typography 
                variant="body2"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
              >
                Travel through time with prompts spanning from 1970s hip-hop culture 
                to contemporary digital street art.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box textAlign="center">
              <Typography 
                variant="h4" 
                color="error" 
                gutterBottom
                sx={{ fontSize: { xs: '2rem', sm: '2.125rem' } }}
              >
                🎨
              </Typography>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
              >
                Style Fusion
              </Typography>
              <Typography 
                variant="body2"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
              >
                Blend different artistic styles, from photorealism to animation, 
                creating unique visual narratives.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/image-prompt-iterator')}
        >
          Back to Image Prompt Tools
        </Button>
      </Box>
    </Container>
  );
};

export default UrbanAgentsGallery;